import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Download, Search, Play, RefreshCw, CheckCircle, Clock, XCircle, Info } from 'lucide-react';
import api from '../api';
import { NafCodeGroup, ImportJob } from '../types';

const FRENCH_REGIONS = [
  'Auvergne-Rhône-Alpes',
  'Bourgogne-Franche-Comté',
  'Bretagne',
  'Centre-Val de Loire',
  'Corse',
  'Grand Est',
  'Hauts-de-France',
  'Île-de-France',
  'Normandie',
  'Nouvelle-Aquitaine',
  'Occitanie',
  'Pays de la Loire',
  'Provence-Alpes-Côte d\'Azur',
];

const FRENCH_DEPARTMENTS = [
  { code: '01', name: 'Ain' }, { code: '02', name: 'Aisne' }, { code: '03', name: 'Allier' },
  { code: '04', name: 'Alpes-de-Haute-Provence' }, { code: '05', name: 'Hautes-Alpes' },
  { code: '06', name: 'Alpes-Maritimes' }, { code: '07', name: 'Ardèche' }, { code: '08', name: 'Ardennes' },
  { code: '09', name: 'Ariège' }, { code: '10', name: 'Aube' }, { code: '11', name: 'Aude' },
  { code: '12', name: 'Aveyron' }, { code: '13', name: 'Bouches-du-Rhône' }, { code: '14', name: 'Calvados' },
  { code: '15', name: 'Cantal' }, { code: '16', name: 'Charente' }, { code: '17', name: 'Charente-Maritime' },
  { code: '18', name: 'Cher' }, { code: '19', name: 'Corrèze' }, { code: '2A', name: 'Corse-du-Sud' },
  { code: '2B', name: 'Haute-Corse' }, { code: '21', name: 'Côte-d\'Or' }, { code: '22', name: 'Côtes-d\'Armor' },
  { code: '23', name: 'Creuse' }, { code: '24', name: 'Dordogne' }, { code: '25', name: 'Doubs' },
  { code: '26', name: 'Drôme' }, { code: '27', name: 'Eure' }, { code: '28', name: 'Eure-et-Loir' },
  { code: '29', name: 'Finistère' }, { code: '30', name: 'Gard' }, { code: '31', name: 'Haute-Garonne' },
  { code: '32', name: 'Gers' }, { code: '33', name: 'Gironde' }, { code: '34', name: 'Hérault' },
  { code: '35', name: 'Ille-et-Vilaine' }, { code: '36', name: 'Indre' }, { code: '37', name: 'Indre-et-Loire' },
  { code: '38', name: 'Isère' }, { code: '39', name: 'Jura' }, { code: '40', name: 'Landes' },
  { code: '41', name: 'Loir-et-Cher' }, { code: '42', name: 'Loire' }, { code: '43', name: 'Haute-Loire' },
  { code: '44', name: 'Loire-Atlantique' }, { code: '45', name: 'Loiret' }, { code: '46', name: 'Lot' },
  { code: '47', name: 'Lot-et-Garonne' }, { code: '48', name: 'Lozère' }, { code: '49', name: 'Maine-et-Loire' },
  { code: '50', name: 'Manche' }, { code: '51', name: 'Marne' }, { code: '52', name: 'Haute-Marne' },
  { code: '53', name: 'Mayenne' }, { code: '54', name: 'Meurthe-et-Moselle' }, { code: '55', name: 'Meuse' },
  { code: '56', name: 'Morbihan' }, { code: '57', name: 'Moselle' }, { code: '58', name: 'Nièvre' },
  { code: '59', name: 'Nord' }, { code: '60', name: 'Oise' }, { code: '61', name: 'Orne' },
  { code: '62', name: 'Pas-de-Calais' }, { code: '63', name: 'Puy-de-Dôme' },
  { code: '64', name: 'Pyrénées-Atlantiques' }, { code: '65', name: 'Hautes-Pyrénées' },
  { code: '66', name: 'Pyrénées-Orientales' }, { code: '67', name: 'Bas-Rhin' }, { code: '68', name: 'Haut-Rhin' },
  { code: '69', name: 'Rhône' }, { code: '70', name: 'Haute-Saône' }, { code: '71', name: 'Saône-et-Loire' },
  { code: '72', name: 'Sarthe' }, { code: '73', name: 'Savoie' }, { code: '74', name: 'Haute-Savoie' },
  { code: '75', name: 'Paris' }, { code: '76', name: 'Seine-Maritime' }, { code: '77', name: 'Seine-et-Marne' },
  { code: '78', name: 'Yvelines' }, { code: '79', name: 'Deux-Sèvres' }, { code: '80', name: 'Somme' },
  { code: '81', name: 'Tarn' }, { code: '82', name: 'Tarn-et-Garonne' }, { code: '83', name: 'Var' },
  { code: '84', name: 'Vaucluse' }, { code: '85', name: 'Vendée' }, { code: '86', name: 'Vienne' },
  { code: '87', name: 'Haute-Vienne' }, { code: '88', name: 'Vosges' }, { code: '89', name: 'Yonne' },
  { code: '90', name: 'Territoire de Belfort' }, { code: '91', name: 'Essonne' },
  { code: '92', name: 'Hauts-de-Seine' }, { code: '93', name: 'Seine-Saint-Denis' },
  { code: '94', name: 'Val-de-Marne' }, { code: '95', name: 'Val-d\'Oise' },
  { code: '971', name: 'Guadeloupe' }, { code: '972', name: 'Martinique' },
  { code: '973', name: 'Guyane' }, { code: '974', name: 'La Réunion' }, { code: '976', name: 'Mayotte' },
];

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const PappersImportPage: React.FC = () => {
  const [nafGroups, setNafGroups] = useState<NafCodeGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [manualNafCodes, setManualNafCodes] = useState('');
  const [region, setRegion] = useState('');
  const [department, setDepartment] = useState('');
  const [minCreationDate, setMinCreationDate] = useState('');
  const [companyType, setCompanyType] = useState('personne_morale');
  const [enrichContacts, setEnrichContacts] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estimate
  const [estimateResult, setEstimateResult] = useState<{ total: number; estimatedCredits: number; enrichCredits?: number; sample: any[] } | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState('');

  // Import
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Jobs
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const fetchNafGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/naf-codes/groups');
      setNafGroups(res.data.data || res.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des groupes NAF.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImportJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.get('/pappers/import-jobs');
      setImportJobs(res.data.data || res.data || []);
    } catch (err) {
      // ignore
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNafGroups();
    fetchImportJobs();
  }, [fetchNafGroups, fetchImportJobs]);

  // Poll active job status
  useEffect(() => {
    if (activeJobId) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/pappers/job/${activeJobId}`);
          const job = res.data;
          fetchImportJobs();
          if (job.status === 'completed' || job.status === 'failed') {
            setActiveJobId(null);
            setImportLoading(false);
            if (job.status === 'completed') {
              setImportMessage(`Import terminé : ${job.createdRecords} entreprises créées, ${job.duplicateRecords} doublons.`);
            } else {
              setImportMessage(`Import échoué. Vérifiez les logs.`);
            }
          }
        } catch (err) {
          // ignore
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeJobId, fetchImportJobs]);

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
    setEstimateResult(null);
  };

  // Parse manual NAF codes from comma/space separated string
  const parsedManualCodes = manualNafCodes
    .split(/[,;\s]+/)
    .map(c => c.trim())
    .filter(c => c.length > 0);

  const handleEstimate = async () => {
    if (selectedGroupIds.length === 0 && parsedManualCodes.length === 0) {
      setEstimateError('Veuillez sélectionner au moins un groupe NAF ou saisir des codes NAF.');
      return;
    }
    setEstimateLoading(true);
    setEstimateError('');
    setEstimateResult(null);
    setImportMessage('');
    try {
      const res = await api.post('/pappers/bulk-import/estimate', {
        nafGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        nafCodes: parsedManualCodes.length > 0 ? parsedManualCodes : undefined,
        region: region || undefined,
        department: department || undefined,
        minCreationDate: minCreationDate || undefined,
        companyType: companyType || undefined,
        enrichContacts,
      });
      setEstimateResult(res.data);
    } catch (err: any) {
      setEstimateError(err.response?.data?.message || 'Erreur lors de l\'estimation.');
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!estimateResult || estimateResult.total === 0) return;
    setImportLoading(true);
    setImportMessage('');
    try {
      const res = await api.post('/pappers/bulk-import', {
        nafGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        nafCodes: parsedManualCodes.length > 0 ? parsedManualCodes : undefined,
        region: region || undefined,
        department: department || undefined,
        minCreationDate: minCreationDate || undefined,
        companyType: companyType || undefined,
        enrichContacts,
      });
      const job = res.data;
      setActiveJobId(job.id);
      setImportMessage('Import en cours... Les entreprises et contacts sont importés automatiquement.');
      fetchImportJobs();
    } catch (err: any) {
      setImportMessage(err.response?.data?.message || 'Erreur lors du lancement de l\'import.');
      setImportLoading(false);
    }
  };

  const getJobStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success"><CheckCircle size={12} /> Terminé</span>;
      case 'processing':
      case 'running':
        return <span className="badge badge-info"><Clock size={12} /> En cours</span>;
      case 'pending':
        return <span className="badge badge-warning"><Clock size={12} /> En attente</span>;
      case 'failed':
      case 'error':
        return <span className="badge badge-danger"><XCircle size={12} /> Erreur</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Download size={28} /> Import Pappers</span>
        </h1>
      </div>

      {error && (
        <div className="login-error mb-3">{error}</div>
      )}

      <div className="two-col">
        <div>
          {/* Direct NAF Code Input - PRIMARY */}
          <div className="card mb-3">
            <div className="card-header">
              <h3>Codes NAF</h3>
              {parsedManualCodes.length > 0 && (
                <span className="badge badge-info">{parsedManualCodes.length} code{parsedManualCodes.length > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="card-body">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Saisissez des codes NAF (séparés par des virgules)</label>
                <input
                  className="form-input"
                  value={manualNafCodes}
                  onChange={(e) => { setManualNafCodes(e.target.value); setEstimateResult(null); }}
                  placeholder="Ex: 25.11Z, 25.62A, 25.73A, 25.99A, 28.41Z"
                  style={{ fontSize: '1rem' }}
                />
              </div>
              {parsedManualCodes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {parsedManualCodes.map((code, i) => (
                    <code key={i} style={{ fontSize: '0.85rem', background: '#EFF6FF', color: '#1D4ED8', padding: '3px 10px', borderRadius: '4px', border: '1px solid #BFDBFE' }}>
                      {code}
                    </code>
                  ))}
                </div>
              )}
              {nafGroups.length > 0 && (
                <details style={{ marginTop: '12px' }}>
                  <summary className="cursor-pointer text-secondary" style={{ fontSize: '0.85rem' }}>
                    Ou sélectionner par groupe ({selectedGroupIds.length} sélectionné{selectedGroupIds.length > 1 ? 's' : ''})
                  </summary>
                  <div className="checkbox-grid" style={{ marginTop: '8px' }}>
                    {nafGroups.map((group) => (
                      <div
                        key={group.id}
                        className={`checkbox-item ${selectedGroupIds.includes(group.id) ? 'checked' : ''}`}
                        onClick={() => handleToggleGroup(group.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGroupIds.includes(group.id)}
                          onChange={() => handleToggleGroup(group.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div>
                          <div className="font-semibold">{group.name}</div>
                          <div className="text-secondary" style={{ fontSize: '0.75rem' }}>
                            {group.items?.length || 0} code{(group.items?.length || 0) > 1 ? 's' : ''} NAF
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-header"><h3>Filtres</h3></div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Région</label>
                  <select
                    className="form-select"
                    value={region}
                    onChange={(e) => { setRegion(e.target.value); setEstimateResult(null); }}
                  >
                    <option value="">Toute la France</option>
                    {FRENCH_REGIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Département</label>
                  <select
                    className="form-select"
                    value={department}
                    onChange={(e) => { setDepartment(e.target.value); setEstimateResult(null); }}
                  >
                    <option value="">Tous les départements</option>
                    {FRENCH_DEPARTMENTS.map((d) => (
                      <option key={d.code} value={d.code}>{d.code} - {d.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Type d'entreprise</label>
                  <select
                    className="form-select"
                    value={companyType}
                    onChange={(e) => { setCompanyType(e.target.value); setEstimateResult(null); }}
                  >
                    <option value="">Toutes</option>
                    <option value="personne_morale">Sociétés uniquement (SAS, SARL, SA...)</option>
                    <option value="personne_physique">Individuelles uniquement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date de création minimum</label>
                  <input
                    type="date"
                    className="form-input"
                    value={minCreationDate}
                    onChange={(e) => { setMinCreationDate(e.target.value); setEstimateResult(null); }}
                  />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-check" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={enrichContacts}
                    onChange={(e) => { setEnrichContacts(e.target.checked); setEstimateResult(null); }}
                  />
                  <span>
                    <strong>Enrichir les dirigeants</strong>
                    <span className="text-secondary" style={{ fontSize: '0.8rem', display: 'block' }}>
                      Récupère les contacts (gérants, présidents...) via l'API Pappers (+1 crédit par entreprise)
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div className="card-footer" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button className="btn" onClick={handleEstimate} disabled={estimateLoading || importLoading}>
                {estimateLoading ? <span className="spinner spinner-sm" /> : <><Search size={16} /> Estimer</>}
              </button>
              {estimateResult && estimateResult.total > 0 && (
                <button className="btn btn-primary" onClick={handleBulkImport} disabled={importLoading}>
                  {importLoading ? <span className="spinner spinner-sm" /> : <><Play size={16} /> Importer tout ({estimateResult.total})</>}
                </button>
              )}
            </div>
          </div>

          {estimateError && (
            <div className="login-error mb-3">{estimateError}</div>
          )}

          {importMessage && (
            <div
              className={`mb-3 ${importMessage.includes('terminé') || importMessage.includes('cours') ? 'badge-success' : 'badge-danger'}`}
              style={{ padding: '10px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
            >
              {importMessage}
            </div>
          )}

          {/* Estimate Result */}
          {estimateResult && (
            <div className="card mb-3">
              <div className="card-header"><h3>Résultat de l'estimation</h3></div>
              <div className="card-body">
                <div className="import-stats" style={{ marginBottom: '16px' }}>
                  <div className="import-stat">
                    <div className="import-stat-value">{estimateResult.total.toLocaleString('fr-FR')}</div>
                    <div className="import-stat-label">Entreprises trouvées</div>
                  </div>
                  <div className="import-stat">
                    <div className="import-stat-value">~{estimateResult.estimatedCredits}</div>
                    <div className="import-stat-label">Crédits recherche</div>
                  </div>
                  {enrichContacts && estimateResult.enrichCredits !== undefined && estimateResult.enrichCredits > 0 && (
                    <div className="import-stat">
                      <div className="import-stat-value" style={{ color: '#F59E0B' }}>+{estimateResult.enrichCredits}</div>
                      <div className="import-stat-label">Crédits enrichissement</div>
                    </div>
                  )}
                  <div className="import-stat">
                    <div className="import-stat-value" style={{ color: '#EF4444', fontWeight: 700 }}>
                      ~{estimateResult.estimatedCredits + (enrichContacts && estimateResult.enrichCredits ? estimateResult.enrichCredits : 0)}
                    </div>
                    <div className="import-stat-label">Total crédits</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px', background: '#EFF6FF', borderRadius: '8px', fontSize: '0.85rem' }}>
                  <Info size={16} style={{ flexShrink: 0, marginTop: '2px', color: '#3B82F6' }} />
                  <div>
                    <strong>Ce que l'import va faire :</strong>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      <li>Récupérer toutes les {estimateResult.total} entreprises automatiquement (toutes les pages)</li>
                      <li>Créer les fiches entreprise avec toutes les infos disponibles</li>
                      <li>Créer les contacts dirigeants (gérants, présidents, directeurs) automatiquement</li>
                      <li>Ignorer les doublons (entreprises déjà importées)</li>
                    </ul>
                  </div>
                </div>
                {estimateResult.sample.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div className="text-secondary" style={{ fontSize: '0.8rem', marginBottom: '6px' }}>Aperçu :</div>
                    <div className="import-preview">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>SIREN</th>
                            <th>Nom</th>
                            <th>Ville</th>
                            <th>NAF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {estimateResult.sample.map((r: any, idx: number) => (
                            <tr key={r.siren || idx}>
                              <td>{r.siren || '-'}</td>
                              <td>{r.nom_entreprise || r.denomination || '-'}</td>
                              <td>{r.siege?.ville || '-'}</td>
                              <td>{r.code_naf || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          {/* Import Jobs */}
          <div className="card">
            <div className="card-header">
              <h3>Historique des imports</h3>
              <button className="btn btn-sm" onClick={fetchImportJobs} disabled={jobsLoading}>
                <RefreshCw size={14} /> Actualiser
              </button>
            </div>
            <div className="card-body">
              {jobsLoading ? (
                <div className="spinner-container"><div className="spinner" /></div>
              ) : importJobs.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px' }}>
                  <p className="text-secondary">Aucun import</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {importJobs.map((job) => (
                    <div key={job.id} className="card" style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span className="font-semibold">{job.type || 'Import Pappers'}</span>
                        {getJobStatusBadge(job.status)}
                      </div>
                      <div className="import-stats">
                        <div className="import-stat">
                          <div className="import-stat-value">{job.totalRecords}</div>
                          <div className="import-stat-label">Total</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.processedRecords}</div>
                          <div className="import-stat-label">Traités</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.createdRecords}</div>
                          <div className="import-stat-label">Créés</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.duplicateRecords}</div>
                          <div className="import-stat-label">Doublons</div>
                        </div>
                      </div>
                      {job.totalRecords > 0 && (
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${Math.round((job.processedRecords / job.totalRecords) * 100)}%` }}
                          />
                        </div>
                      )}
                      <div className="text-secondary mt-1" style={{ fontSize: '0.8rem' }}>
                        {formatDate(job.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PappersImportPage;
